import Echo from 'laravel-echo'
import LocalStorageService from './refresh.service'
import { HIDDEN_THRESHOLD_MS } from './refresh/refresh-registry'

window.io = require('socket.io-client')

const WEBSOCKET_URL = process.env.VUE_APP_WEBSOCKET_URL || 'localhost:6001'
// Сколько ждём подключения перед перечитыванием данных. Дольше незачем:
// данные идут по HTTP, сокет нужен только для событий
const ENSURE_CONNECTED_TIMEOUT_MS = 3000
// Как часто сторож проверяет, не пора ли. Сама пауза считается в реестре обновлений
const WATCHDOG_CHECK_MS = 5000
// Тип пакета EVENT в socket.io-parser: только события доказывают доставку, ping и pong
// доказывают лишь транспорт
const SOCKET_IO_EVENT_PACKET = 2

class WebsocketService {
  static echo = null
  initialized = false

  getEcho () {
    if (!WebsocketService.echo) {
      WebsocketService.echo = new Echo({
        broadcaster: 'socket.io',
        host: WEBSOCKET_URL,
        transports: ['websocket'],
        auth: {
          headers: {
            // Геттер, а не готовая строка: при переподписке после разрыва Echo
            // берёт заголовки отсюда. Строка осталась бы со старым токеном, и
            // приватные каналы молча перестали бы получать события.
            // Объект headers не заменять: kkm-proxy дописывает в него свой Token
            get Authorization () {
              return 'Bearer ' + LocalStorageService.getToken()
            },
          },
        },
      })
    }

    return WebsocketService.echo
  }

  getSocket () {
    return this.getEcho().connector.socket
  }

  isConnected () {
    return this.getSocket().connected
  }

  // Если socket.io уже переподключается сам, connect() ничего не делает -
  // второго подключения не будет
  ensureConnected (timeoutMs = ENSURE_CONNECTED_TIMEOUT_MS) {
    const socket = this.getSocket()

    if (socket.connected) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer)
        socket.off('connect', done)
        resolve()
      }
      const timer = setTimeout(done, timeoutMs)

      socket.on('connect', done)
      socket.connect()
    })
  }

  // Вызывается один раз. Показывает в сторе, есть ли связь, и перечитывает данные
  // там, где события могли пройти мимо: после разрыва, после долгого отсутствия на
  // вкладке, при появлении сети и по сторожу. Перед тяжёлыми запросами реестр
  // спрашивает версию выборки и перечитывает только при изменении
  init (store, refreshService) {
    if (this.initialized) {
      return
    }
    this.initialized = true

    const socket = this.getSocket()
    const probe = () => refreshService.run({ silent: true, probe: true })
    let hadDisconnect = false
    let hiddenAt = null
    let disconnectedWhileHidden = false

    // Если первое подключение не удастся, события disconnect не будет - отмечаем
    // заранее, иначе полоса «Нет связи» не появится
    if (!socket.connected) {
      store.commit('ws/setConnected', false)
    }

    // Любой принятый пакет-событие - доставка работает, сторожу нечего проверять
    socket.io.on('packet', (packet) => {
      if (packet.type === SOCKET_IO_EVENT_PACKET) {
        refreshService.markAlive()
      }
    })

    socket.on('connect', () => {
      store.commit('ws/setConnected', true)

      // Первое подключение пропускаем: экраны только что загрузили данные сами
      if (!hadDisconnect) {
        return
      }
      hadDisconnect = false
      this.resubscribeChannels()
      // Разброс: после рестарта echo-server все вкладки не должны спрашивать сервер разом
      setTimeout(probe, refreshService.reconnectDelayMs())
    })

    socket.on('disconnect', (reason) => {
      hadDisconnect = true
      if (hiddenAt !== null) {
        disconnectedWhileHidden = true
      }
      store.commit('ws/setConnected', false)

      // После явного закрытия сервером socket.io 2.x сам не переподключается
      if (reason === 'io server disconnect') {
        socket.connect()
      }
    })

    // Короткое отвлечение не стоит ничего: события дошли и в скрытую вкладку.
    // Перечитываем, если отсутствовали долго или сокет за это время падал
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        hiddenAt = Date.now()
        disconnectedWhileHidden = false
        return
      }
      const hiddenFor = hiddenAt === null ? 0 : Date.now() - hiddenAt
      const stale = disconnectedWhileHidden || hiddenFor >= HIDDEN_THRESHOLD_MS
      hiddenAt = null
      disconnectedWhileHidden = false
      if (stale) {
        probe()
      }
    })
    window.addEventListener('online', probe)

    // Сторож: решение в реестре, здесь только тик
    setInterval(() => {
      const action = refreshService.watchdogAction({
        visible: document.visibilityState === 'visible',
        wsConnected: socket.connected,
      })
      if (action === 'probe') {
        refreshService.run({ silent: true, watchdog: true, probe: true })
      } else if (action === 'full') {
        refreshService.run({ silent: true, watchdog: true })
      }
    }, WATCHDOG_CHECK_MS)
  }

  // Echo переподписывается сам только при автоматическом реконнекте. Если сервер
  // закрыл соединение и мы подключились вручную, каналы остались бы без подписки.
  // Подписаться на тот же канал второй раз безопасно
  resubscribeChannels () {
    Object.values(this.getEcho().connector.channels).forEach((channel) => channel.subscribe())
  }

  listenEvent (channel, event, calback) {
    this.getEcho()
      .channel(channel)
      .listen(event, calback)
    return this
  }

  stopListening (channel, event, callback = null) {
    this.getEcho()
      .channel(channel)
      .stopListening(event, callback)
    return this
  }
}

export const websocketService = new WebsocketService()
